import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Heart, Users, MessageCircle, ArrowRight, CheckCircle } from "lucide-react";

const EndOfLifeCoaching = () => {
  return (
    <>
      <Helmet>
        <title>End-of-Life Coaching Columbia, MO | Care Coordination | Serenity Passage</title>
        <meta
          name="description"
          content="End-of-life coaching and hospice support in Columbia, Missouri. Compassionate emotional guidance and healthcare advocacy for families in Mid-Missouri."
        />
        <meta
          name="keywords"
          content="end-of-life coaching Columbia MO, hospice support Columbia Missouri, patient advocacy Mid-Missouri, healthcare advocacy, death doula, emotional support"
        />
        <link rel="canonical" href="https://www.serenitypassage.net/end-of-life-coaching" />
      </Helmet>

      {/* Hero Banner */}
      <section className="bg-gradient-to-b from-rose-100 to-rose-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-serif text-gray-800 text-center mb-4">
            End-of-Life Coaching and Care Coordination
          </h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto">
            Compassionate emotional support and advocacy through your journey
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              End-of-life coaching provides compassionate emotional support and advocacy across healthcare settings, ensuring your voice and values remain central throughout your journey. I am here to walk beside you, offering presence, understanding, and practical guidance.
            </p>

            <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
              <Heart className="w-7 h-7 text-rose-600" />
              Emotional Support
            </h2>

            <p className="text-gray-700 mb-6">
              Facing end-of-life brings a range of emotions for both the individual and their loved ones. I provide a safe, non-judgmental space to process feelings, fears, and hopes.
            </p>

            <ul className="space-y-4 mb-12">
              {[
                "One-on-one supportive conversations",
                "Processing grief, fear, and uncertainty",
                "Finding meaning and peace in the journey",
                "Supporting family members through their emotions",
                "Creating space for difficult but important conversations",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
              <Users className="w-7 h-7 text-rose-600" />
              Healthcare Advocacy
            </h2>

            <p className="text-gray-700 mb-6">
              Navigating healthcare systems during this time can feel overwhelming. I serve as your advocate, helping ensure your wishes are communicated and respected.
            </p>

            <ul className="space-y-4 mb-12">
              {[
                "Accompanying you to medical appointments when desired",
                "Helping communicate your wishes to healthcare providers",
                "Facilitating family meetings to discuss care decisions",
                "Ensuring your advance directives are honored",
                "Bridging communication between family and medical teams",
                "Discuss hospice vs palliative care options",
                "Help schedule meet and greets with selected organizations and provide assistance with admission process",
                "Collaborate with hospice and palliative care providers to ensure care is client-centered",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
              <MessageCircle className="w-7 h-7 text-rose-600" />
              Holistic Approach
            </h2>

            <p className="text-gray-700 mb-8">
              I believe in caring for the whole person: mind, body, and spirit. My coaching integrates emotional, practical, and spiritual support tailored to your unique needs and beliefs.
            </p>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-rose-50 to-rose-100 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-serif text-gray-800 mb-4">
              You Don't Have to Walk This Path Alone
            </h3>
            <p className="text-gray-600 mb-6">
              Your first 30-minute consultation is complimentary.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-8 py-4 rounded-full font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              Schedule a Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default EndOfLifeCoaching;
